// Global Instructions Rule Applied!
//
// useNewCommitmentForm — the state of the New commitment form
// (/commitments/forms/full/new). The form is opened with any subset of
//   ?account=<id>     the account is filled in and fixed
//   ?investor=<id>    the investor entity (and its account) are fixed
//   ?fund=<id>        a standalone fund is fixed
//   ?fund_group=<id>  a consolidated fund is fixed
// and creates, in one save, a standalone commitment (fund__save_commitment)
// or a consolidated commitment (fg__save_subscription) — optionally with a
// new investor entity under the account. On Create the "new" tab becomes the
// created record's form. The carry marker ("Receives carried interest") is
// offered for a standalone fund only; with it on, the commitment may be 0.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import { summariseFieldErrors } from '../../../core/utils/utilities.ts'
import { fundCarryDefaultsOf } from '../../../core/utils/fundCarryDefaults.ts'
import type { FieldValue } from '../../../core/components/InputManager/types.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type { RootState } from '../../../state_manager/store.ts'
import { TABLE_COMMITMENTS, TABLE_CONSOLIDATED_COMMITMENTS } from '../../../state_manager/globals.ts'
import { transformFormTab, updateTabTitle } from '../../../store/slices/tabSlice'
import type { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { fetchCompanyById } from '../../companies/services/companiesService.ts'
import { fetchInvestorDetail } from '../../investors/services/investorsService.ts'
import { saveFundCommitment } from '../../funds/services/fundsService.ts'
import { saveFundGroupSubscription } from '../../fund_groups/services/fundGroupsService.ts'
import {
  CommitmentFieldKeys,
  EMPTY_CORE_DRAFT,
  EMPTY_NEW_INVESTOR_DRAFT,
  EMPTY_NOTICE_DRAFT,
  EMPTY_TERMS_DRAFT,
  allocationsFromMembers,
  applyCoreChange,
  applyTermsChange,
  buildConsolidatedCreatePayload,
  buildStandaloneCreatePayload,
  commitmentAmountAllowed,
  summariseAllocations,
  type AllocationRowDraft,
  type CommitmentCoreDraft,
  type CommitmentNoticeDraft,
  type CommitmentTermsDraft,
  type InvestorChoice,
  type NewInvestorDraft
} from '../utils/commitmentDrafts.ts'
import {
  CARRY_CONSOLIDATED_HELP,
  CARRY_RECIPIENT_HELP,
  GP_COMMITMENT_HELP,
  GP_CONSOLIDATED_HELP,
  NEW_INVESTOR_VALUE,
  getCarryRecipientField,
  getCommitmentFields,
  getGpField,
  getNewCommitmentInvestorFields,
  getNewInvestorFields,
  getTargetFields
} from '../utils/commitmentFieldConfigs.ts'
import { applyDefaultAllocations, patchAllocationRow } from './useConsolidatedCommitmentForm.tsx'
import { useCommitmentFormOptions } from './useCommitmentFormOptions.ts'

const VALIDATION_TOAST_KEY = 'new-commitment-form-required'
const FUND_PREFIX = 'fund:'
const GROUP_PREFIX = 'group:'

type AccountValue = { value: string; label: string }

/** The company select hands back an option object, an id, or nothing. */
const toAccountValue = (value: FieldValue): AccountValue | null => {
  if (value && typeof value === 'object' && !Array.isArray(value) && value.value) {
    return { value: String(value.value), label: String(value.label ?? value.name ?? '') }
  }
  if (typeof value === 'string' && value) return { value, label: '' }
  return null
}

const numberOrNull = (value: FieldValue): number | null => {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export function useNewCommitmentForm() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { closeFormTab } = useTabManagerContext()

  // New-record tabs are keyed per prefill query (tabSlice.addFormTab). Snapshot
  // the mount-time search string: the global location later reflects whichever
  // tab is active, not this one.
  const [mountSearch] = useState(() => location.search)
  const formTabKey = `${TABLE_COMMITMENTS}-form-new-new${mountSearch}`
  const [prefill] = useState(() => {
    const query = new URLSearchParams(mountSearch)
    return {
      accountId: query.get('account'),
      investorId: query.get('investor'),
      fundId: query.get('fund'),
      fundGroupId: query.get('fund_group')
    }
  })

  const classifications = useSelector((state: RootState) => state.globalConfigs?.investor_classifications)
  const sbicInvestorTypes = useSelector((state: RootState) => state.globalConfigs?.sbic_investor_types)

  const [account, setAccount] = useState<AccountValue | null>(null)
  const [investor, setInvestor] = useState<string | null>(prefill.investorId)
  const [newInvestor, setNewInvestor] = useState<NewInvestorDraft>(EMPTY_NEW_INVESTOR_DRAFT)
  const [target, setTarget] = useState<string | null>(
    prefill.fundGroupId ? `${GROUP_PREFIX}${prefill.fundGroupId}` : prefill.fundId ? `${FUND_PREFIX}${prefill.fundId}` : null
  )
  const [core, setCore] = useState<CommitmentCoreDraft>(EMPTY_CORE_DRAFT)
  const [terms, setTerms] = useState<CommitmentTermsDraft>(EMPTY_TERMS_DRAFT)
  const [notice, setNotice] = useState<CommitmentNoticeDraft>(EMPTY_NOTICE_DRAFT)
  const [allocations, setAllocations] = useState<AllocationRowDraft[]>([])
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [prefillLoading, setPrefillLoading] = useState(prefill.accountId != null || prefill.investorId != null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [validationErrorsVersion, setValidationErrorsVersion] = useState(0)
  // a new investor entity is named after its account only until the user names it
  const nameTouchedRef = useRef(false)

  const { options, investors, investorsReady, statusOptions, noticeSupplementOptions } = useCommitmentFormOptions(
    account?.value ?? null
  )

  const accountLocked = prefill.accountId != null || prefill.investorId != null
  const investorLocked = prefill.investorId != null
  const targetLocked = prefill.fundId != null || prefill.fundGroupId != null

  useEffect(() => {
    dispatch(updateTabTitle({ key: formTabKey, title: 'New Commitment' }))
  }, [dispatch, formTabKey])

  // The prefilled investor / account, read once.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const token = getAccessToken(store.getState().auth)
      if (prefill.investorId) {
        const res = await fetchInvestorDetail(prefill.investorId, token)
        if (cancelled) return
        if (!res.data || !res.data.accounts__id) {
          showErrorToast(res.error || 'The investor could not be loaded')
        } else {
          setAccount({ value: res.data.accounts__id, label: res.data.accounts__name ?? '' })
        }
      } else if (prefill.accountId) {
        const res = await fetchCompanyById(prefill.accountId, token)
        if (cancelled) return
        if (res.error || !res.data) {
          showErrorToast(res.error || 'The account could not be loaded')
        } else {
          setAccount({ value: prefill.accountId, label: String(res.data[AccountRecordFields.accounts__name] ?? '') })
        }
      }
      if (!cancelled) setPrefillLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [prefill])

  // An account with no investor entity yet: the commitment creates one.
  useEffect(() => {
    if (!account || !investorsReady || investor != null) return
    if (investors.length === 0) setInvestor(NEW_INVESTOR_VALUE)
  }, [account, investorsReady, investors, investor])

  // A new investor entity is usually named after its account.
  useEffect(() => {
    if (investor !== NEW_INVESTOR_VALUE || nameTouchedRef.current || !account?.label) return
    setNewInvestor((prev) => (prev.name ? prev : { ...prev, name: account.label }))
  }, [investor, account])

  // ---------------------------------------------------------------- the target
  const fundOption = useMemo(
    () =>
      target?.startsWith(FUND_PREFIX)
        ? (options?.funds.find((f) => f.value === target.slice(FUND_PREFIX.length)) ?? null)
        : null,
    [target, options]
  )
  const fundGroupOption = useMemo(
    () =>
      target?.startsWith(GROUP_PREFIX)
        ? (options?.fund_groups.find((g) => g.value === target.slice(GROUP_PREFIX.length)) ?? null)
        : null,
    [target, options]
  )
  const kind: 'fund' | 'consolidated' | null = fundOption ? 'fund' : fundGroupOption ? 'consolidated' : null
  const currencySymbol = fundOption?.currencies__symbol ?? fundGroupOption?.currencies__symbol ?? null
  const isSbic = (fundOption?.funds__is_sbic ?? fundGroupOption?.funds__is_sbic) === true
  // a ?fund= that is a member of a consolidated fund is not offered: its
  // commitments are added on the consolidated fund
  const targetUnavailable = target != null && options != null && kind == null

  // a consolidated fund's member funds, everything in the primary fund to start
  const fundGroupId = fundGroupOption?.value ?? null
  useEffect(() => {
    setAllocations(fundGroupOption ? allocationsFromMembers(fundGroupOption.members) : [])
    // only when another consolidated fund is chosen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundGroupId])

  const committedStatusId = useMemo(() => {
    const committed = statusOptions.find((s) => s.label === 'Committed')
    return typeof committed?.value === 'number' ? committed.value : null
  }, [statusOptions])

  const targetOptions = useMemo<SelectListEntry[]>(
    () => [
      ...(options?.funds ?? []).map((f) => ({ value: `${FUND_PREFIX}${f.value}`, label: f.label })),
      // a consolidated fund says so, and names its member funds, under its name
      ...(options?.fund_groups ?? []).map((g) => ({
        value: `${GROUP_PREFIX}${g.value}`,
        label: g.label,
        shortDescription: `Consolidated fund: ${g.members.map((m) => m.funds__name ?? '').filter(Boolean).join(', ')}`
      }))
    ],
    [options]
  )

  const investorOptions = useMemo<SelectListEntry[]>(
    () => investors.map((i) => ({ value: i.value, label: i.label })),
    [investors]
  )
  // what "use the investor's setting" means for the chosen investor
  const investorTemplateIds = useMemo(
    () => investors.find((i) => i.value === investor)?.prog__notice_supplement_template_ids ?? [],
    [investors, investor]
  )

  // -------------------------------------------------------------- field configs
  const accountFields = useMemo(
    () =>
      getNewCommitmentInvestorFields(
        { account, investor },
        { investorOptions, accountLocked, investorLocked }
      ).slice(0, 1),
    [account, investor, investorOptions, accountLocked, investorLocked]
  )
  const investorFields = useMemo(
    () =>
      getNewCommitmentInvestorFields(
        { account, investor },
        { investorOptions, accountLocked, investorLocked }
      ).slice(1),
    [account, investor, investorOptions, accountLocked, investorLocked]
  )
  const newInvestorFields = useMemo(
    () =>
      investor === NEW_INVESTOR_VALUE
        ? getNewInvestorFields(newInvestor, {
            classificationOptions: (classifications ?? []).map((c) => ({ value: c.id, label: c.label })),
            sbicInvestorTypeOptions: (sbicInvestorTypes ?? []).map((t) => ({ value: t.id, label: t.label })),
            isSbic
          })
        : [],
    [investor, newInvestor, classifications, sbicInvestorTypes, isSbic]
  )
  const targetFields = useMemo(
    () => getTargetFields(target, { targetOptions, locked: targetLocked }),
    [target, targetOptions, targetLocked]
  )
  const commitmentFields = useMemo(
    () => [
      ...getCommitmentFields(core, { statusOptions, currencySymbol }),
      ...getGpField(core, kind === 'consolidated' ? GP_CONSOLIDATED_HELP : GP_COMMITMENT_HELP),
      // the carry marker: on a consolidated commitment it sets every pocket
      ...getCarryRecipientField(core, kind === 'consolidated' ? CARRY_CONSOLIDATED_HELP : CARRY_RECIPIENT_HELP)
    ],
    [core, statusOptions, currencySymbol, kind]
  )

  // ------------------------------------------------------------------- changes
  const clearError = useCallback((key: string) => {
    setValidationErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const handleChange = useCallback(
    (key: string, value: FieldValue) => {
      setTouched(true)
      clearError(key)
      // an amount of 0 is refused or not by the carry marker
      if (key === CommitmentFieldKeys.is_carry_recipient) clearError(CommitmentFieldKeys.amount)
      switch (key) {
        case CommitmentFieldKeys.account:
          setAccount(toAccountValue(value))
          // another account has other investor entities
          setInvestor(null)
          break
        case CommitmentFieldKeys.investor:
          setInvestor(value == null || value === '' ? null : String(value))
          break
        case CommitmentFieldKeys.target:
          setTarget(value == null || value === '' ? null : String(value))
          break
        case CommitmentFieldKeys.new_investor_name:
          nameTouchedRef.current = true
          setNewInvestor((prev) => ({ ...prev, name: value == null ? null : String(value) }))
          break
        case CommitmentFieldKeys.new_investor_legal_name:
          setNewInvestor((prev) => ({ ...prev, legal_name: value == null ? null : String(value) }))
          break
        case CommitmentFieldKeys.new_investor_classification:
          setNewInvestor((prev) => ({ ...prev, investor_classification_id: numberOrNull(value) }))
          break
        case CommitmentFieldKeys.new_investor_sbic_type:
          setNewInvestor((prev) => ({ ...prev, sbic_investor_type_id: numberOrNull(value) }))
          break
        default:
          setCore((prev) => applyCoreChange(prev, key, value))
          setTerms((prev) => applyTermsChange(prev, key, value))
      }
    },
    [clearError]
  )

  const handleNoticeChange = useCallback((next: CommitmentNoticeDraft) => {
    setTouched(true)
    setNotice(next)
  }, [])

  const handleAllocationChange = useCallback(
    (memberId: string, patch: Partial<Pick<AllocationRowDraft, 'amount' | 'status_id' | 'date_closed'>>) => {
      setTouched(true)
      setAllocations((prev) => patchAllocationRow(prev, memberId, patch, committedStatusId))
    },
    [committedStatusId]
  )

  const handleApplyDefaults = useCallback(() => {
    if (core.amount == null) return
    setTouched(true)
    setAllocations((prev) => applyDefaultAllocations(prev, core.amount ?? 0, committedStatusId))
  }, [core.amount, committedStatusId])

  /** Cancel (and Close): a new commitment's tab closes. */
  const handleCancel = useCallback(() => {
    dismissErrorToast(VALIDATION_TOAST_KEY)
    closeFormTab(formTabKey)
  }, [closeFormTab, formTabKey])

  // ---------------------------------------------------------------------- save
  const handleSave = useCallback(async () => {
    // Required-field gate: per-field red text + one toast naming the fields,
    // and the InputManager scrolls to and focuses the first bad field.
    const errors: Record<string, string> = {}
    if (!account) errors[CommitmentFieldKeys.account] = 'Account is required'
    if (!investor) errors[CommitmentFieldKeys.investor] = 'Investor is required'
    if (investor === NEW_INVESTOR_VALUE && !(newInvestor.name ?? '').trim()) {
      errors[CommitmentFieldKeys.new_investor_name] = 'Investor name is required'
    }
    if (!kind) errors[CommitmentFieldKeys.target] = 'Fund is required'
    if (kind) {
      // 0 is an amount on the commitment that receives the carried interest
      if (!commitmentAmountAllowed(core.amount, core.is_carry_recipient)) {
        errors[CommitmentFieldKeys.amount] = 'Commitment is required'
      }
      if (core.status_id == null) errors[CommitmentFieldKeys.status_id] = 'Status is required'
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      setValidationErrorsVersion((v) => v + 1)
      showErrorToast(summariseFieldErrors([errors]) ?? 'Please complete the required fields', {
        key: VALIDATION_TOAST_KEY
      })
      return
    }
    if (!account || !investor || !kind) return
    // (a commitment of 0 - the carry's own account - has no split to check:
    // the save gives every member fund a pocket of 0)
    if (kind === 'consolidated' && core.amount !== 0 && summariseAllocations(allocations, core, null, '').residual <= 0) {
      showErrorToast('The satellite allocations must leave a positive amount in the primary fund', {
        key: VALIDATION_TOAST_KEY
      })
      return
    }
    dismissErrorToast(VALIDATION_TOAST_KEY)

    const choice: InvestorChoice =
      investor === NEW_INVESTOR_VALUE ? { accountId: account.value, newInvestor } : { investorId: investor }
    const token = getAccessToken(store.getState().auth)

    setSaving(true)
    try {
      let module: string
      let createdId: string | undefined
      if (kind === 'fund' && fundOption) {
        const res = await saveFundCommitment(
          buildStandaloneCreatePayload(fundOption.value, choice, { core, terms, notice }),
          false,
          token
        )
        if (!res.success) {
          showErrorToast(res.error || 'Failed to create the commitment')
          return
        }
        module = TABLE_COMMITMENTS
        createdId = res.fundCommitmentId
      } else if (fundGroupOption) {
        const res = await saveFundGroupSubscription(
          buildConsolidatedCreatePayload(fundGroupOption.value, choice, { core, notice, allocations }),
          false,
          token
        )
        if (!res.success) {
          showErrorToast(res.error || 'Failed to create the consolidated commitment')
          return
        }
        module = TABLE_CONSOLIDATED_COMMITMENTS
        createdId = res.fundGroupCommitmentId
      } else {
        return
      }

      toast.success(kind === 'fund' ? 'Commitment added' : 'Consolidated commitment added')
      if (!createdId) {
        closeFormTab(formTabKey)
        return
      }
      // Morph the "new" tab into the created record's tab in place; the pane
      // remounts on the new key and cold-loads the record.
      dispatch(transformFormTab({ key: formTabKey, module, recordId: createdId }))
      navigate(`/${module}/forms/full/${createdId}`, { replace: true })
    } finally {
      setSaving(false)
    }
  }, [
    account,
    investor,
    newInvestor,
    kind,
    core,
    terms,
    notice,
    allocations,
    fundOption,
    fundGroupOption,
    dispatch,
    navigate,
    closeFormTab,
    formTabKey
  ])

  return {
    loading: prefillLoading,
    kind,
    targetUnavailable,
    accountKey: account?.value ?? 'none',
    accountFields,
    investorFields,
    newInvestorFields,
    targetFields,
    commitmentFields,
    core,
    terms,
    notice,
    allocations,
    statusOptions,
    noticeSupplementOptions,
    investorTemplateIds,
    currencySymbol,
    fundFeeRate: fundOption?.funds__mgmt_fee_rate ?? null,
    fundDefaults: fundCarryDefaultsOf(fundOption),
    dirty: touched,
    saving,
    validationErrors,
    validationErrorsVersion,
    handleChange,
    handleNoticeChange,
    handleAllocationChange,
    handleApplyDefaults,
    handleCancel,
    handleSave
  }
}
